<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useToggle } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { formatNumber } from '@chatwoot/utils';
import wootConstants from 'dashboard/constants/globals';
import Auth from 'dashboard/api/auth';
import {
  detectSearchType,
  SEARCH_TYPE_LABEL,
} from 'dashboard/helper/conversationSearchHelper';

import ConversationBasicFilter from './widgets/conversation/ConversationBasicFilter.vue';
import SwitchLayout from 'dashboard/routes/dashboard/conversation/search/SwitchLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  pageTitle: { type: String, required: true },
  hasAppliedFilters: { type: Boolean, required: true },
  hasActiveFolders: { type: Boolean, required: true },
  activeStatus: { type: String, required: true },
  isOnExpandedLayout: { type: Boolean, required: true },
  conversationStats: { type: Object, required: true },
  isListLoading: { type: Boolean, required: true },
  // Cinta inteligente (Victor 2026-05-30): cantidad de resultados filtrados
  // que el parent calculó (length del conversationList). Se muestra en la
  // caption bajo el input cuando el usuario está buscando.
  searchResultsCount: { type: Number, default: null },
  isSearching: { type: Boolean, default: false },
});

const emit = defineEmits([
  'addFolders',
  'deleteFolders',
  'resetFilters',
  'basicFilterChange',
  'filtersModal',
  'searchEnter',
]);

const searchQuery = defineModel('searchQuery', { type: String, default: '' });

// Buscador elevado "glass" (Victor 2026-06-12 · mockup P2): atajo ⌘K / Ctrl+K
// enfoca el buscador, igual que el chip que muestra la caja levantada.
const searchInput = ref(null);
const onSearchShortcut = e => {
  if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
    e.preventDefault();
    searchInput.value?.focus();
  }
};
onMounted(() => window.addEventListener('keydown', onSearchShortcut));
onUnmounted(() => window.removeEventListener('keydown', onSearchShortcut));

const { uiSettings, updateUISettings } = useUISettings();

const onBasicFilterChange = (value, type) => {
  emit('basicFilterChange', value, type);
};

// === Menú "⋮" de celular (Propuesta P1 · Victor 2026-06-03) ===
// En celular agrupa Filtrar · Ordenar/Canal · Cerrar sesión en un solo botón
// para que el header no se vea apretado ni inconsistente.
const [showMobileMenu, toggleMobileMenu] = useToggle();
const openFiltersFromMobileMenu = () => {
  toggleMobileMenu(false);
  emit('filtersModal');
};
const logout = () => {
  toggleMobileMenu(false);
  Auth.logout();
};

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return props.hasAppliedFilters || props.hasActiveFolders;
});

// Cinta inteligente · 2026-05-30 (Victor): detecta qué tipo de búsqueda
// está aplicando (@usuario, teléfono, email, nombre). El icono de la
// izquierda del input cambia para indicarlo (la lupa se convierte en
// 👤/📞/✉/📷) y debajo aparece una caption con tipo + contador. Texto
// del input SIEMPRE 100% visible — sin chips que tapen lo que se escribe.
const SEARCH_TYPE_ICON = {
  social: '📷',
  phone: '📞',
  email: '✉',
  name: '👤',
};
const searchType = computed(() => detectSearchType(searchQuery.value));
const searchTypeIcon = computed(() =>
  searchType.value ? SEARCH_TYPE_ICON[searchType.value] : null
);
const searchTypeLabel = computed(() =>
  searchType.value ? SEARCH_TYPE_LABEL[searchType.value] : null
);
const showSearchCaption = computed(() => Boolean(searchType.value));
const resultsCountLabel = computed(() => {
  if (props.isSearching) return 'Buscando…';
  const n = props.searchResultsCount;
  if (n === null || n === undefined) return null;
  return `${n} ${n === 1 ? 'resultado' : 'resultados'}`;
});

const allCount = computed(() => props.conversationStats?.allCount || 0);
const formattedAllCount = computed(() => formatNumber(allCount.value));

const toggleConversationLayout = () => {
  const { LAYOUT_TYPES } = wootConstants;
  const {
    conversation_display_type: conversationDisplayType = LAYOUT_TYPES.CONDENSED,
  } = uiSettings.value;
  const newViewType =
    conversationDisplayType === LAYOUT_TYPES.CONDENSED
      ? LAYOUT_TYPES.EXPANDED
      : LAYOUT_TYPES.CONDENSED;
  updateUISettings({
    conversation_display_type: newViewType,
    previously_used_conversation_display_type: newViewType,
  });
};
</script>

<template>
  <div>
  <div
    class="flex items-center justify-between gap-2 px-3 h-[3.25rem]"
    :class="{
      'border-b border-n-strong': hasAppliedFiltersOrActiveFolders,
    }"
  >
    <div class="flex items-center shrink-0 min-w-0">
      <h1
        class="text-base font-medium truncate text-n-slate-12"
        :title="pageTitle"
      >
        {{ pageTitle }}
      </h1>
      <span
        v-if="
          allCount > 0 && hasAppliedFiltersOrActiveFolders && !isListLoading
        "
        class="px-2 py-1 my-0.5 mx-1 rounded-md capitalize bg-n-slate-3 text-xxs text-n-slate-12 shrink-0"
        :title="allCount"
      >
        {{ formattedAllCount }}
      </span>
      <span
        v-if="!hasAppliedFiltersOrActiveFolders"
        class="px-2 py-1 my-0.5 mx-1 rounded-md capitalize bg-n-slate-3 text-xxs text-n-slate-12 shrink-0"
      >
        {{ $t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${activeStatus}.TEXT`) }}
      </span>
    </div>

    <!--
      Tabs en la MISMA fila (Victor 2026-06-12 · mockup P1 "una sola fila"): los
      filtros (Todos/Mi Chat/Urgentes/departamentos) llegan como slot desde ChatList
      y viven entre el brand y el buscador → adiós "filtros arriba de filtros". Solo
      en escritorio (md+); en celular los tabs siguen yendo en su propia fila.
    -->
    <div class="hidden md:flex flex-1 items-center min-w-0">
      <slot name="tabs" />
    </div>

    <!--
      Buscador elevado "glass" (Victor 2026-06-12 · mockup P2 "chat-barra-buscador"):
      caja levantada con sombra + brillo interior arriba (relieve), fondo #1a1a20,
      esquinas 12px y chip ⌘K a la derecha. El relieve la hace destacar del header.
    -->
    <div
      v-if="!hasAppliedFiltersOrActiveFolders"
      class="flex flex-1 md:flex-none md:w-72 items-center gap-2.5 px-3.5 h-10 min-w-0 rounded-xl border border-n-weak bg-[#1a1a20] shadow-[0_8px_22px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.05)] transition-colors focus-within:border-amber-400 focus-within:ring-2 focus-within:ring-amber-400/30"
      :class="{ 'border-amber-400/50': searchType }"
    >
      <!--
        Cinta inteligente · 2026-05-30 (Victor): icono izquierdo dinámico.
        Sin texto detectado → lupa neutra. Con tipo detectado → emoji dorado
        que indica qué tipo de búsqueda se aplica (👤/📞/✉/📷). El texto del
        input NUNCA se tapa con chip · esa señal vive en la caption inferior.
      -->
      <span
        v-if="searchTypeIcon"
        :title="`Buscando por ${searchTypeLabel}`"
        class="shrink-0 text-sm leading-none select-none"
        style="color: #c9a35a;"
      >{{ searchTypeIcon }}</span>
      <svg
        v-else
        class="size-4 shrink-0 text-n-slate-10"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke-width="1.5"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
        />
      </svg>
      <input
        ref="searchInput"
        v-model="searchQuery"
        type="text"
        :placeholder="$t('CHAT_LIST.SEARCH_PLACEHOLDER')"
        class="reset-base flex-1 min-w-0 p-0 m-0 h-full text-[13px] leading-none bg-transparent border-none appearance-none text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:shadow-none"
        @keyup.enter="emit('searchEnter')"
      />
      <kbd
        v-if="!searchQuery"
        class="shrink-0 hidden md:inline-flex items-center text-[10px] leading-none font-sans text-n-slate-10 border border-n-weak rounded-md px-1.5 py-0.5 select-none"
      >⌘K</kbd>
      <button
        v-if="searchQuery"
        class="shrink-0 text-n-slate-10 hover:text-n-slate-12"
        @click="searchQuery = ''"
      >
        <svg
          class="size-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M6 18 18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
    <div class="flex items-center gap-1">
      <template v-if="hasAppliedFilters && !hasActiveFolders">
        <div class="relative">
          <NextButton
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON')"
            icon="i-lucide-save"
            slate
            xs
            faded
            @click="emit('addFolders')"
          />
          <div
            id="saveFilterTeleportTarget"
            class="absolute z-50 mt-2"
            :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
          />
        </div>
        <NextButton
          v-tooltip.top-end="$t('FILTER.CLEAR_BUTTON_LABEL')"
          icon="i-lucide-circle-x"
          ruby
          faded
          xs
          @click="emit('resetFilters')"
        />
      </template>
      <template v-if="hasActiveFolders">
        <div class="relative">
          <NextButton
            id="toggleConversationFilterButton"
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.EDIT.EDIT_BUTTON')"
            icon="i-lucide-pen-line"
            slate
            xs
            faded
            @click="emit('filtersModal')"
          />
          <div
            id="conversationFilterTeleportTarget"
            class="fixed z-[100] top-[120px] ltr:left-[130px] rtl:right-[130px]"
          />
        </div>
        <NextButton
          id="toggleConversationFilterButton"
          v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.DELETE.DELETE_BUTTON')"
          icon="i-lucide-trash-2"
          ruby
          xs
          faded
          @click="emit('deleteFolders')"
        />
      </template>
      <!-- Filtro y Orden: en PC quedan sueltos; en celular se mueven al menú ⋮ -->
      <div v-else class="relative hidden md:block">
        <NextButton
          id="toggleConversationFilterButton"
          v-tooltip.right="$t('FILTER.TOOLTIP_LABEL')"
          icon="i-lucide-list-filter"
          slate
          xs
          faded
          @click="emit('filtersModal')"
        />
        <div
          id="conversationFilterTeleportTarget"
          class="absolute z-50 mt-2"
          :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
        />
      </div>
      <ConversationBasicFilter
        v-if="!hasAppliedFiltersOrActiveFolders"
        class="hidden md:flex"
        :is-on-expanded-layout="isOnExpandedLayout"
        @change-filter="onBasicFilterChange"
      />
      <SwitchLayout
        :is-on-expanded-layout="isOnExpandedLayout"
        @toggle="toggleConversationLayout"
      />

      <!-- Menú ⋮ · solo celular (P1): Filtrar · Ordenar/Canal · Cerrar sesión -->
      <div class="relative md:hidden">
        <NextButton
          icon="i-lucide-ellipsis-vertical"
          slate
          xs
          faded
          @click="toggleMobileMenu()"
        />
        <div
          v-if="showMobileMenu"
          v-on-click-outside="() => toggleMobileMenu(false)"
          class="mt-2 bg-n-solid-3 border border-n-strong w-60 rounded-xl p-2 absolute z-[60] top-full ltr:right-0 rtl:left-0 shadow-[0_18px_48px_rgba(0,0,0,0.6)] max-h-[80vh] overflow-y-auto"
        >
          <button
            v-if="!hasAppliedFiltersOrActiveFolders"
            type="button"
            class="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm text-left text-n-slate-12 hover:bg-n-alpha-2"
            @click="openFiltersFromMobileMenu"
          >
            <span class="i-lucide-list-filter size-4" />
            <span class="flex-1">{{ $t('FILTER.TOOLTIP_LABEL') }}</span>
          </button>
          <div
            v-if="!hasAppliedFiltersOrActiveFolders"
            class="my-1.5 border-t border-n-weak"
          />
          <ConversationBasicFilter
            v-if="!hasAppliedFiltersOrActiveFolders"
            embedded
            :is-on-expanded-layout="isOnExpandedLayout"
            @change-filter="onBasicFilterChange"
          />
          <div
            v-if="!hasAppliedFiltersOrActiveFolders"
            class="my-1.5 border-t border-n-weak"
          />
          <button
            type="button"
            class="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm text-left text-n-ruby-11 hover:bg-n-alpha-2"
            @click="logout"
          >
            <span class="i-lucide-log-out size-4" />
            <span class="flex-1">{{ $t('SIDEBAR_ITEMS.LOGOUT') }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!--
    Caption "Buscando por X · N resultados" (Victor 2026-05-30).
    Solo aparece cuando hay texto en el buscador. Margen pequeño,
    paleta dorada M1.B, hairline superior para separar de la barra.
  -->
  <div
    v-if="showSearchCaption"
    class="flex items-center justify-between px-4 py-1.5 text-[10px] font-semibold border-t border-n-weak/40"
    style="background: rgba(201, 163, 90, 0.045);"
  >
    <span class="inline-flex items-center gap-1.5" style="color: #c9a35a;">
      <span
        class="inline-block w-1 h-1 rounded-full"
        style="background: #c9a35a;"
      />
      Buscando por {{ searchTypeLabel }}
    </span>
    <span v-if="resultsCountLabel" class="text-n-slate-11">
      {{ resultsCountLabel }}
    </span>
  </div>
  </div>
</template>
